'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import debounce from 'lodash.debounce';

import BoardPickerModal from './Boards/BoardPickerModal.react';

import BoardStore from '../../stores/BoardStore';
import BoardActions from '../../actions/BoardActions';

import Analytics from '../../utils/Analytics';

import styles from './FavoriteButton.scss';

export default class FavoriteButton extends Component {
    static propTypes = {
        recipe: PropTypes.object,
        plan: PropTypes.object,
        combo: PropTypes.object,
        food: PropTypes.object,
        collection: PropTypes.object,
        onFavorite: PropTypes.func,
        onBeforeUnfavorite: PropTypes.func,

        icon: PropTypes.string,
        className: PropTypes.string,
    };

    static defaultProps = {
        icon: 'icon-heart',
        iconBoarded: 'icon-heart',
        className: 'favorite-btn',
        showIcon: true,
        boardedText: "",
        notBoardedText: "",
    };

    static contextTypes = {
        breakpoint47em: PropTypes.bool,
        isFavoritesSearch: PropTypes.bool
    };

    constructor(props) {
        super(props);

        const { recipe, plan, combo, product, food, collection } = props;
        const { uuid, type } = recipe || plan || combo || food || collection || {};

        this.state = {
            isPopoverOpen: false,
            firstBoard: null,
            autoPopulateInMealFeed: false,
            saving: false,
            boarded: BoardStore.getBoardsByResourceId(uuid).length > 0,
        };

        this.debounceOnClick = debounce(this.onClick, 300);

    }

    componentDidMount = () => {
        const { plan } = this.props;

        if (plan && plan.uuid) {
            const resources = BoardStore.getBoardsByResourceId(plan.uuid);

            const resource = resources[0]?.contents?.find(resource => resource.resource_id == plan.uuid);

            if (resource) {
                this.setState({autoPopulateInMealFeed: !resource.exclude_from_lena});
            }
        }
        BoardStore.addChangeListener(this.onBoardStoreChange);
    }


    componentWillUnmount = () => {
        BoardStore.removeChangeListener(this.onBoardStoreChange);
    }

    onBoardStoreChange = () => {
        const { recipe, plan, combo, product, food, collection } = this.props;
        const { uuid, type } = recipe || plan || combo || food || collection || {};
        let { saving } = this.state;

        const boarded = BoardStore.getBoardsByResourceId(uuid).length > 0;
        saving = plan && boarded && saving ? false : saving;

        this.setState({boarded, saving});
    }

    UNSAFE_componentWillReceiveProps = (nextProps) => {
        const { recipe, plan, combo, food, collection } = this.props;

        const curDoc = recipe || plan || combo || food || collection;
        const newDoc = nextProps.recipe || nextProps.plan || nextProps.combo || nextProps.food || nextProps.collection;

        if (newDoc != curDoc) {
            // Document changed - recalculate boarded status with new UUID
            const { uuid } = newDoc || {};
            if (uuid) {
                const boarded = BoardStore.getBoardsByResourceId(uuid).length > 0;
                this.setState({firstBoard: null, isPopoverOpen: false, boarded});
            } else {
                this.setState({firstBoard: null, isPopoverOpen: false});
            }
        }
    }

    onClick = async (ev) => {
        const { onFavorite, onBeforeUnfavorite, recipe, plan, combo, food, collection } = this.props;
        const { uuid } = recipe || plan || combo || food || collection || {};

        // If onFavorite prop is provided, use it (modern behavior)
        if (onFavorite) {
            // Check if item is currently boarded
            const isBoarded = BoardStore.getBoardsByResourceId(uuid).length > 0;

            // If item is boarded and we have onBeforeUnfavorite, call it first
            if (isBoarded && onBeforeUnfavorite) {
                const shouldBlock = await onBeforeUnfavorite();

                if (shouldBlock) {
                    return; // Blocked by onBeforeUnfavorite (e.g., dietitian recommendation)
                }
            }

            this.setState({saving: true});
            await onFavorite();
            this.setState({saving: false});
            return;
        }

        // Otherwise, use legacy behavior
        this.applyFavorite();
    }

    applyFavorite = () => {
        let { isPopoverOpen } = this.state;
        const { recipe, plan, combo, product, food, collection, onFavorite, onBeforeUnfavorite } = this.props;
        const { uuid, type } = recipe || plan || combo || food || collection || {};

        const isBoarded = BoardStore.getBoardsByResourceId(uuid).length > 0;

        if (!isPopoverOpen && isBoarded) {

            if (onBeforeUnfavorite && onBeforeUnfavorite()) {
                return;
            }

            this.setState({isPopoverOpen: true});

            return;
        } else if (!isPopoverOpen && !isBoarded) {
            // We're going to add this item to a default board
            const lastBoard = BoardStore.getDefaultBoard();

            const newItem = {
                resource_id: uuid,
                resource_type: type,
            };

            if (lastBoard.links) {
                // Board already exists, just add to it.
                BoardActions.addToBoard(lastBoard, [newItem]);
            } else {
                lastBoard.contents = lastBoard.contents || [];
                lastBoard.contents.push(newItem);


                // Board does not exist, create a whole new one
                BoardActions.upsertBoards([lastBoard]);
            }

            // That's not confusing. It's the Last Board that was used,
            // but we want that one to be the First Board in the list...
            this.setState({ firstBoard: lastBoard });

            Analytics.saveFavorite(lastBoard, recipe || plan || combo || food || collection);
        } else {
            this.setState({isPopoverOpen: false});
        }
    }

    closePopover = () => {
        this.setState({isPopoverOpen: false});
    }

    isPlanInBoard = (board) => {
        const { plan } = this.props

        if (!plan?.uuid) {
            return false;
        }

        return (board.contents || []).filter(content => content.resource_id === plan.uuid).length > 0;
    }

    renderBoardPickerModal = () => {
        const { isPopoverOpen, firstBoard } = this.state;
        const { recipe, plan, combo, food, collection, className } = this.props;

        if (!isPopoverOpen) {
            return null;
        }

        return <BoardPickerModal closeModal={this.closePopover}
                    recipe={recipe} plan={plan} combo={combo} food={food} collection={collection}
                    firstBoard={firstBoard} />
    }

    handleClick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        this.debounceOnClick();
    };

    render() {
        const { recipe, plan, combo, collection, food, icon, iconBoarded, className, isMealCard, showIcon, boardedText, notBoardedText } = this.props;
        const { firstBoard, autoPopulateInMealFeed, saving } = this.state;
        const { isFavoritesSearch } = this.context;
        const uuid = (recipe && recipe.uuid) ||
                     (plan && plan.uuid) ||
                     (combo && combo.uuid) ||
                     (collection && collection.uuid) ||
                     (food && food.uuid);

        if (!uuid || !BoardStore.isLoaded()) {
            return <span />
        }

        // Use prop boarded if provided (from parent like Editor), otherwise use internal state
        let boarded = this.props.boarded !== undefined ? this.props.boarded : this.state.boarded;
        boarded = boarded || saving;

        const button = (
            <button onClick={this.handleClick} className={className} data-boarded={boarded && !isFavoritesSearch}>
                {isMealCard && showIcon ? (
                    isFavoritesSearch && boarded ? (
                        <i className=" feather feather-trash-2" />
                    ) : (
                        <i className="icon-heart1">
                            <i className={boarded ? iconBoarded : icon} />
                        </i>
                    )
                ) : null}
                {!isMealCard && showIcon ? (
                    isFavoritesSearch && boarded ? (
                        <i className="feather feather-trash-2" />
                    ) : (
                        <i className={boarded ? iconBoarded : icon} />
                    )
                ) : null}
                {!showIcon && boarded ? boardedText : null}
                {!showIcon && !boarded ? notBoardedText : null}
                <span className="assistive-text">Favorite</span>
                {this.props.children}
            </button>
        );

        return (
            <span>
                {this.renderBoardPickerModal()}
                {button}
            </span>
        );
    }
}
